import { useState, useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Plus, ArrowUp, Trash2, Save, RotateCcw } from 'lucide-react'
import type { TeamTaskRow } from '../api'
import { TranslatableText } from '@/components/ui/TranslatableText'

function renumberTasks(rows: TeamTaskRow[]): TeamTaskRow[] {
    return rows.map((r, i) => ({ ...r, taskNo: i + 1 }))
}

export interface TeamTaskTableProps {
    tasks: TeamTaskRow[]
    onSave: (tasks: TeamTaskRow[]) => void
    isSaving?: boolean
    /** When true (default), call onSave after every change (runbook-style auto-save) */
    autoUpdate?: boolean
    /** Called when user toggles auto-update (runbook-style). If not provided, auto-update is always on. */
    onAutoUpdateChange?: (autoUpdate: boolean) => void
}

export function TeamTaskTable({
    tasks,
    onSave,
    isSaving = false,
    autoUpdate = true,
    onAutoUpdateChange,
}: TeamTaskTableProps) {
    const { t } = useTranslation()
    const [localTasks, setLocalTasks] = useState<TeamTaskRow[]>(() => renumberTasks(tasks ?? []))
    const [editingIndex, setEditingIndex] = useState<number | null>(null)
    const [editingValue, setEditingValue] = useState('')
    const editTextareaRef = useRef<HTMLTextAreaElement>(null)
    const onSaveRef = useRef(onSave)
    const autoUpdateRef = useRef(autoUpdate)
    const localTasksRef = useRef(localTasks)

    useEffect(() => {
        localTasksRef.current = localTasks
    }, [localTasks])

    useEffect(() => {
        onSaveRef.current = onSave
        autoUpdateRef.current = autoUpdate
    }, [onSave, autoUpdate])

    useEffect(() => {
        setLocalTasks(renumberTasks(tasks ?? []))
        setEditingIndex(null)
    }, [tasks])

    useEffect(() => {
        if (editingIndex !== null && editTextareaRef.current) {
            editTextareaRef.current.focus()
            editTextareaRef.current.select()
        }
    }, [editingIndex])

    const handleReset = useCallback(() => {
        setLocalTasks(renumberTasks(tasks ?? []))
    }, [tasks])

    const handleTaskChange = useCallback((index: number, value: string) => {
        setLocalTasks((prev) => {
            const next = prev.map((r, i) =>
                i === index ? { ...r, task: value } : r
            )
            if (autoUpdateRef.current) {
                onSaveRef.current(renumberTasks(next))
            }
            return next
        })
    }, [])

    const handleStartEdit = useCallback((index: number) => {
        setEditingValue(localTasks[index]?.task ?? '')
        setEditingIndex(index)
    }, [localTasks])

    const handleSaveEdit = useCallback((index: number) => {
        const value = editingValue.trim()
        const current = localTasks[index]?.task ?? ''
        if (value !== current) {
            handleTaskChange(index, value)
        }
        setEditingIndex(null)
    }, [editingValue, localTasks, handleTaskChange])

    const handleCancelEdit = useCallback(() => {
        setEditingValue('')
        setEditingIndex(null)
    }, [])

    const handleAddTask = useCallback(() => {
        setLocalTasks((prev) => {
            const next = [...prev, { taskNo: prev.length + 1, task: '' }]
            if (autoUpdateRef.current) {
                onSaveRef.current(renumberTasks(next))
            }
            return renumberTasks(next)
        })
    }, [])

    const handleInsertTask = useCallback((index: number) => {
        setLocalTasks((prev) => {
            const next = [...prev.slice(0, index), { taskNo: index + 1, task: '' }, ...prev.slice(index)]
            const renumbered = renumberTasks(next)
            if (autoUpdateRef.current) {
                onSaveRef.current(renumbered)
            }
            return renumbered
        })
    }, [])

    const handleDeleteTask = useCallback((index: number) => {
        setLocalTasks((prev) => {
            const next = prev.filter((_, i) => i !== index)
            const renumbered = renumberTasks(next)
            if (autoUpdateRef.current) {
                onSaveRef.current(renumbered)
            }
            return renumbered
        })
    }, [])

    const showToolbar = onAutoUpdateChange != null

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-4">
                    {showToolbar && (
                        <div className="flex items-center gap-2">
                            <Switch
                                id="auto-update"
                                checked={autoUpdate}
                                onCheckedChange={onAutoUpdateChange}
                            />
                            <Label htmlFor="auto-update" className="text-sm">
                                {t('objects.runbook.autoUpdate', 'Auto Update')}
                            </Label>
                        </div>
                    )}
                    {showToolbar && !autoUpdate && (
                        <>
                            <Button type="button" variant="outline" size="sm" onClick={handleReset}>
                                <RotateCcw className="mr-2 h-4 w-4" />
                                {t('objects.runbook.reset', 'Reset')}
                            </Button>
                            <Button
                                type="button"
                                variant="default"
                                size="sm"
                                onClick={() => onSaveRef.current(renumberTasks(localTasksRef.current))}
                                disabled={isSaving}
                            >
                                <Save className="mr-2 h-4 w-4" />
                                {isSaving ? t('objects.runbook.saving', 'Saving...') : t('objects.runbook.save', 'Save')}
                            </Button>
                        </>
                    )}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={handleAddTask} disabled={isSaving}>
                    <Plus className="mr-2 h-4 w-4" />
                    {t('teams.addTask', 'Add task')}
                </Button>
            </div>
            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-table-header border-b border-table-border">
                            <TableHead className="font-semibold text-foreground w-14">{t('teams.taskNo', 'No')}</TableHead>
                            <TableHead className="font-semibold text-foreground">{t('teams.task', 'Task')}</TableHead>
                            <TableHead className="font-semibold text-foreground w-[100px] text-right">
                                {t('common.actions', 'Actions')}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {localTasks.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                                    {t('teams.noTasks', 'No tasks. Click "Add task" to add one.')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            localTasks.map((row, index) => (
                                <TableRow key={index}>
                                    <TableCell className="text-muted-foreground font-bold">{row.taskNo}</TableCell>
                                    <TableCell>
                                        <div
                                            className="w-full min-w-0"
                                            onClick={(e) => {
                                                if (editingIndex !== index) {
                                                    e.stopPropagation()
                                                    handleStartEdit(index)
                                                }
                                            }}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            {editingIndex === index ? (
                                                <Textarea
                                                    ref={editTextareaRef}
                                                    value={editingValue}
                                                    onChange={(e) => setEditingValue(e.target.value)}
                                                    onBlur={() => handleSaveEdit(index)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Escape') {
                                                            e.preventDefault()
                                                            handleCancelEdit()
                                                        }
                                                        if (e.key === 'Enter' && e.ctrlKey) {
                                                            e.preventDefault()
                                                            handleSaveEdit(index)
                                                        }
                                                    }}
                                                    placeholder={t('teams.taskPlaceholder', 'Task description')}
                                                    rows={3}
                                                    className="min-h-[4.5rem] w-full text-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 resize-y"
                                                    onClick={(e) => e.stopPropagation()}
                                                    onMouseDown={(e) => e.stopPropagation()}
                                                />
                                            ) : (
                                                <div
                                                    className="min-h-[4.5rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm flex items-start cursor-text hover:border-ring transition-colors"
                                                    style={{ minWidth: 0 }}
                                                >
                                                    <span className="text-sm whitespace-pre-wrap break-words w-full block">
                                                        {row.task ? <TranslatableText text={row.task} /> : <span className="text-muted-foreground">—</span>}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <div
                                            className="flex items-center justify-end gap-1"
                                            onClick={(e) => e.stopPropagation()}
                                            onMouseDown={(e) => e.stopPropagation()}
                                        >
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    e.preventDefault()
                                                    handleInsertTask(index)
                                                }}
                                                onMouseDown={(e) => {
                                                    e.stopPropagation()
                                                    e.preventDefault()
                                                }}
                                                title={t('objects.runbook.insertAbove', 'Insert above')}
                                            >
                                                <ArrowUp className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2 text-red-500 hover:text-red-600"
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    e.preventDefault()
                                                    handleDeleteTask(index)
                                                }}
                                                onMouseDown={(e) => {
                                                    e.stopPropagation()
                                                    e.preventDefault()
                                                }}
                                                title={t('objects.runbook.delete', 'Delete')}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
